'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { logger } from '@/shared/infrastructure/logging/logger';

export interface AnalyzePhotoResult {
  age?: string;
  gender?: string;
  physicalDescription: string;
  personalityTraits?: string;
  tokensUsed: number;
}

export async function analyzeCharacterPhoto(
  imageBase64: string,
  language?: string,
): Promise<ActionResult<AnalyzePhotoResult>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { TokenWorkflowRunner } = await import('@/contexts/subscriptions/services/TokenWorkflowRunner');
    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');

    const languageMap: Record<string, string> = {
      ru: 'Russian',
      en: 'English',
      cs: 'Czech',
      uk: 'Ukrainian',
    };
    const resolvedLanguage = languageMap[language || 'en'] || 'English';

    const tokenManager = getTokenManager();
    const reserveAmount = await UnifiedAIConfigService.getOperationReservation('character_photo_analysis');
    const result = await new TokenWorkflowRunner(tokenManager).run(
      {
        familyId,
        reserveAmount,
        operationType: 'character_photo_analysis',
        metadata: { createdBy: userId },
      },
      async (reservation) => {
        const { CharacterPromptLoader } = await import(
          '@/contexts/characters/services/prompts/promptLoader'
        );
        const prompt = CharacterPromptLoader.getPrompt('character-description-from-photo.md', {
          LANGUAGE: resolvedLanguage,
        });
        logger.info('[analyzeCharacterPhoto] Calling vision, base64 length:', imageBase64.length);
        // Always through the engine — the worker's vision.analyze handles both
        // real and fake (FakeImageClient) providers, so the portal never talks
        // to a provider itself (no in-Next VisionService).
        const { runVisionAnalyze } = await import('@/shared/infrastructure/workflows/llmRun');
        // Vision provider/model = admin ai_config.vision (same knob as the page critic).
        const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
        const visionCfg = (await UnifiedAIConfigService.getConfig()).vision;
        // a charged provider failure inside the run (Gemini block) → covered COGS on this hold, success or not
        const { billedFailuresOf, trackBilledFailuresCovered } = await import('@/shared/infrastructure/workflows/billedFailures');
        let out: Awaited<ReturnType<typeof runVisionAnalyze>>;
        try {
          out = await runVisionAnalyze({
            startKey: `vision-photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            imageBase64,
            mimeType: 'image/jpeg',
            prompt,
            provider: visionCfg?.provider,
            model: visionCfg?.model,
          });
        } catch (e) {
          await trackBilledFailuresCovered(tokenManager, reservation.id, billedFailuresOf(e));
          throw e;
        }
        await trackBilledFailuresCovered(tokenManager, reservation.id, out.billedFailures);
        const visionResult: { text: string } = { text: out.text };
        logger.info('[analyzeCharacterPhoto] Result:', visionResult.text?.length ?? 0, 'chars');

        // Parse JSON response from vision
        let parsed: Record<string, string>;
        try {
          const jsonStr = visionResult.text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
          parsed = JSON.parse(jsonStr);
        } catch {
          // Fallback: treat entire response as physicalDescription
          logger.warn('[analyzeCharacterPhoto] Failed to parse JSON, using raw text');
          return { physicalDescription: visionResult.text, tokensUsed: 5 };
        }

        // Never auto-fill name from the image — user names the character.
        return {
          age: parsed.age || undefined,
          gender: parsed.gender || undefined,
          physicalDescription: parsed.physicalDescription || visionResult.text,
          personalityTraits: parsed.personalityTraits || undefined,
          tokensUsed: 5,
        };
      },
    );

    return { success: true, data: result };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('analyzeCharacterPhoto', e) };
  }
}
